import { useState, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import Modal from '../Modal/index';
import { Enable2FASetup } from '../Enable2FASetup/Enable2FASetup';
import { ReactComponent as LockIcon } from '../Enable2FASetup/assets/lock.svg';
import { ReactComponent as CheckIcon } from '../Enable2FASetup/assets/check.svg';
import { API } from '../../API';
import { showRequestError } from '../../utils/requestError';
import { TWO_FA_MANDATORY_DATE } from '../../constants/twoFactorAuth';
import styles from './Mandatory2FAModal.module.scss';

const PHASE_INFO = 'info';
const PHASE_SETUP = 'setup';
const PHASE_SUCCESS = 'success';

const formatDate = (date, locale = 'en-GB') => {
  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export const Mandatory2FAModal = ({ isVisible, onClose, onEnabled, onLater }) => {
  const { t, i18n } = useTranslation();
  const [phase, setPhase] = useState(PHASE_INFO);
  const [otpData, setOtpData] = useState(null);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleEnableClick = useCallback(() => {
    setLoading(true);
    API.fetch('POST', '/API/v1/2fa')
      .then((data) => {
        setOtpData(data);
        setPhase(PHASE_SETUP);
      })
      .catch((err) => showRequestError(err, t('2FA setup')))
      .finally(() => setLoading(false));
  }, [t]);

  const handleConfirm = useCallback(() => {
    const codeStr = code.join('');
    if (codeStr.length < 6) return;

    setLoading(true);
    setError(null);
    API.fetch('POST', '/API/v1/2fa/confirmation', null, {
      one_time_password: codeStr,
    })
      .then(() => {
        setPhase(PHASE_SUCCESS);
      })
      .catch(() => {
        // Pass the raw translation key; Enable2FASetup resolves it via t(error).
        setError('Incorrect code. Check the code in your authenticator app and try again');
      })
      .finally(() => setLoading(false));
  }, [code, t]);

  const handleDone = useCallback(() => {
    onEnabled();
  }, [onEnabled]);

  const handleCancel = useCallback(() => {
    setPhase(PHASE_INFO);
    setCode(['', '', '', '', '', '']);
    setError(null);
    setOtpData(null);
  }, []);

  const handleLater = useCallback(() => {
    onLater();
    onClose();
  }, [onLater, onClose]);

  if (phase === PHASE_INFO || phase === PHASE_SUCCESS) {
    const isSuccess = phase === PHASE_SUCCESS;

    return (
      <Modal
        isVisible={isVisible}
        onClose={isSuccess ? handleDone : handleLater}
        doNotCloseOnOutsideClick
        className={styles.modalInfo}
        content={
          <div className={styles.infoContent}>
            <div className={isSuccess ? styles.iconWrapperSuccess : styles.iconWrapperLock}>
              {isSuccess ? <CheckIcon /> : <LockIcon />}
            </div>
            <h2 className={styles.infoTitle}>
              {isSuccess
                ? t('Two-factor authentication enabled')
                : t('Enable two-factor authentication')
              }
            </h2>
            <p className={styles.infoDescription}>
              {isSuccess
                ? t('Your account is now protected.')
                : <Trans
                    i18nKey="To keep your account secure, 2FA will be required starting {{date}}. Setup takes about 2 minutes."
                    values={{ date: formatDate(TWO_FA_MANDATORY_DATE, i18n.language) }}
                    components={{ strong: <strong /> }}
                  />
              }
            </p>
            <div className={styles.buttonGroup}>
              {isSuccess ? (
                <button
                  type="button"
                  className={styles.primaryButton}
                  onClick={handleDone}
                >
                  {t('Done')}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className={styles.primaryButton}
                    onClick={handleEnableClick}
                    disabled={loading}
                  >
                    {t('Enable 2FA')}
                  </button>
                  <button
                    type="button"
                    className={styles.outlinedButton}
                    onClick={handleLater}
                  >
                    {t("I'll do it later")}
                  </button>
                </>
              )}
            </div>
          </div>
        }
      />
    );
  }

  return (
    <Modal
      isVisible={isVisible}
      onClose={handleCancel}
      doNotCloseOnOutsideClick
      className={styles.modalSetup}
      title={t('Enable two-factor authentication')}
      content={
        <div className={styles.setupBody}>
          <Enable2FASetup
            otpData={otpData}
            code={code}
            onCodeChange={setCode}
            error={error}
          />
        </div>
      }
      footer={
        <div className={styles.setupFooter}>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={handleCancel}
          >
            {t('Cancel')}
          </button>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={handleConfirm}
            disabled={code.join('').length < 6 || loading}
            style={{ width: 'auto' }}
          >
            {t('Confirm')}
          </button>
        </div>
      }
    />
  );
};
